import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../../store/store";
import AddAccessForm from "./addAccessForm";
import SearchUserTable from "./SearchUserTable";
import EditAccessForm from "./EditAccessForm";
import BreadCrumbs from "../../components/common/BreadCrumbs/BreadCrumbs";
import Loader from "../../components/common/Loader";
import CommonErrorBanner from "../../components/common/CommonErrorBanner";
import {
  EditEmployee,
  searchManageAccessUsers,
} from "../../store/slices/manageAccessSlice";

interface User {
  lastName: string;
  firstName: string;
  nuid: string;
  location: string;
}

const ManageAccessPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const searchResults = useSelector(
    (state: RootState) => state.manageAccess.searchResults ?? [],
  );
  const searchLoading = useSelector(
    (state: RootState) => state.manageAccess.searchLoading ?? false,
  );
  const searchTotalItems = useSelector(
    (state: RootState) => (state.manageAccess as any).searchTotalItems ?? 0,
  );
  const loggedInRegionCode = useSelector(
    (state: RootState) =>
      (state as any).employee?.employeeDetails?.regionCode ?? "",
  );
  const [showTable, setShowTable] = useState(false);
  const [editingUser, setEditingUser] = useState<EditEmployee | null>(null);
  const [editMode, setEditMode] = useState<"add" | "edit">("add");
  const [showAddSuccess, setShowAddSuccess] = useState(false);
  const [showAddError, setShowAddError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    "Something went wrong. Please try again.",
  );
  const [errorHeader, setErrorHeader] = useState("Error");
  const [errorVariant, setErrorVariant] = useState("Error");
  const [successMessage, setSuccessMessage] = useState("");
  const [lastSearchParams, setLastSearchParams] = useState<{
    region: string;
    searchKey?: string;
    value?: string;
    fName?: string;
    lName?: string;
  } | null>(null);
  const [sortColumn, setSortColumn] = useState("lastName");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [tablePage, setTablePage] = useState(1);
  const formRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showTable && !searchLoading) {
      setTimeout(
        () =>
          tableRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          }),
        50,
      );
    }
  }, [searchLoading, showTable]);

  useEffect(() => {
    if (!showAddSuccess) return;
    const t = setTimeout(() => setShowAddSuccess(false), 5000);
    const s = setTimeout(
      () =>
        bannerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        }),
      100,
    );
    return () => {
      clearTimeout(t);
      clearTimeout(s);
    };
  }, [showAddSuccess]);

  useEffect(() => {
    if (!showAddError) return;
    const t = setTimeout(() => setShowAddError(false), 5000);
    const s = setTimeout(
      () =>
        bannerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        }),
      100,
    );
    return () => {
      clearTimeout(t);
      clearTimeout(s);
    };
  }, [showAddError]);
  const handleSearch = (params?: any) => {
    const region = params?.region?.id ?? params?.region?.value ?? "";
    if (!region) return;
    const hasFName = !!params?.fName;
    const hasLName = !!params?.lName;
    const hasNameSearch = hasFName || hasLName;
    const searchParams =
      params?.searchBy?.value === "nuid" && params?.nuid
        ? { region, searchKey: "nuid", value: params.nuid }
        : {
            region,
            ...(hasNameSearch ? { searchKey: "name" } : {}),
            fName: params?.fName ?? undefined,
            lName: params?.lName ?? undefined,
          };
    setLastSearchParams(searchParams);
    setSortColumn("lastName");
    setSortDirection("asc");
    setTablePage(1);
    dispatch(
      searchManageAccessUsers({
        ...searchParams,
        sortBy: "lastName",
        sortOrder: "asc",
        page: 1,
        perPage: 10,
      }),
    );
    setShowTable(true);
  };

  const handleSortChange = (col: string, dir: string) => {
    if (!lastSearchParams) return;
    setSortColumn(col);
    setSortDirection(dir as "asc" | "desc");
    setTablePage(1);
    dispatch(
      searchManageAccessUsers({
        ...lastSearchParams,
        sortBy: col,
        sortOrder: dir,
        page: 1,
        perPage: 10,
      }),
    );
  };

  const handlePageChange = (page: number) => {
    if (!lastSearchParams) return;
    setTablePage(page);
    dispatch(
      searchManageAccessUsers({
        ...lastSearchParams,
        sortBy: sortColumn,
        sortOrder: sortDirection,
        page,
        perPage: 10,
      }),
    );
  };

  const handleAddClick = (user: User) => {
    const selectedSearchRegionCode =
      lastSearchParams?.region ?? loggedInRegionCode;
    const isCrossRegionAdd =
      !!loggedInRegionCode &&
      !!selectedSearchRegionCode &&
      selectedSearchRegionCode !== loggedInRegionCode;

    // Map User to EditEmployee shape
    const employee: EditEmployee = {
      nuid: user.nuid,
      privRole: isCrossRegionAdd ? "View Only" : "Administrator",
      firstName: user.firstName,
      lastName: user.lastName,
      middleName: "",
      primaryRegion: selectedSearchRegionCode,
      kpAreaCd: "",
      kpSvcArea: "",
      glLocCd: "",
      entity: "",
      respAreaId: 0,
      responsibilityRegion: selectedSearchRegionCode,
      responsibilityArea: "All",
      responsibilityCenter: "All",
      responsibilityLocation: "All",
      responsibilityRegionDesc: user.location,
      responsibilityAreaDesc: "All",
      responsibilityCenterDesc: "All",
      responsibilityLocationDesc: "All",
    };
    setEditingUser(employee);
    setEditMode("add");
    setTimeout(
      () =>
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      50,
    );
  };

  const handleEditClose = () => {
    setEditingUser(null);
    setTimeout(
      () =>
        tableRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        }),
      50,
    );
  };

  const handleBreadcrumbPrevious = (): void => {
    navigate("/home");
  };

  return (
    <div className="min-h-screen">
      <div className="lg:mx-40! mx-5! flex flex-col gap-8">
        <BreadCrumbs
          previousPage="UBT Home"
          onPreviousClick={handleBreadcrumbPrevious}
        />
        <div className="margin-top-2u margin-bottom-2u w-full min-h-48!">
          <div>
            <h1 className="h1 mt-8!">Manage Access</h1>
            <p className="p3 -book">
              Click search to search for employees by their names and region.
            </p>
          </div>
          {showAddSuccess && (
            <div ref={bannerRef}>
              <CommonErrorBanner
                messageInfo={successMessage}
                heading="Success"
                variant="Success"
              />
            </div>
          )}
          {showAddError && (
            <div ref={bannerRef}>
              <CommonErrorBanner
                variant={errorVariant as 'Error' | 'Alert' | 'Success' | 'Informational'}
                heading={errorHeader}
                messageInfo={errorMessage}
              />
            </div>
          )}
          {/* Add a simple input for search term */}
          <AddAccessForm
            onSearch={(params) => handleSearch(params)}
            onReset={() => {
              setShowTable(false);
              setEditingUser(null);
            }}
          />
        </div>
        {searchLoading && <Loader />}
        {showTable && !searchLoading && (
          <div ref={tableRef}>
            <SearchUserTable
              onAdd={handleAddClick}
              onSortChange={handleSortChange}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              currentPage={tablePage}
              setCurrentPage={handlePageChange}
              totalItems={searchTotalItems}
              activeNuid={editingUser?.nuid}
              activeMode={editMode}
              users={searchResults.map((u) => ({
                lastName: u.lastName,
                firstName: u.firstName,
                nuid: u.nuid,
                location: u.location ?? "",
              }))}
            />
          </div>
        )}

        {editingUser && (
          <div ref={formRef}>
            <div>
              <h1 className="h1 mt-8!">
                {editMode === "edit" ? "Editing" : "Adding"}
              </h1>
            </div>
            <EditAccessForm
              employee={editingUser}
              onClose={handleEditClose}
              onSave={(_updated: any) => {
                const msg =
                  editMode === "add"
                    ? "This employee has been assigned successfully to this role and scope of access."
                    : "This employee has been updated successfully to this role and scope of access.";
                setEditingUser(null);
                setShowAddError(false);
                setSuccessMessage(msg);
                setShowAddSuccess(true);
              }}
              onError={(message, header, variant) => {
                setShowAddSuccess(false);
                setErrorMessage(
                  message ?? "Something went wrong. Please try again.",
                );
                setErrorHeader(header ?? "Error");
                setErrorVariant(variant ?? "Error");
                setShowAddError(true);
              }}
              mode={editMode}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageAccessPage;
